import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { FinancialDashboard } from '../components/ui/financial-dashboard';
import {
  Ship, LogOut, Calendar, MapPin, Anchor, Lock, Mail, FileText, Check,
  Loader2, ChevronLeft, ChevronRight, Eye, FileDown, CreditCard, Navigation,
  Users, Waves, Sun, Compass, FileSignature
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOIS_FR = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
const MOIS_SHORT = ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc'];
const JOURS = ['Dim','Lun','Mar','Mer','Jeu','Ven','Sam'];

function formatDateFr(d) {
  if (!d) return '';
  const parts = d.split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return d;
}

// ─── Owner Auth Context ───
const OwnerCtx = createContext(null);
function useOwner() { return useContext(OwnerCtx); }

function OwnerProvider({ children }) {
  const [owner, setOwner] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('owner_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.get(`${API}/owner/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setOwner(r.data))
        .catch(() => { localStorage.removeItem('owner_token'); setToken(null); })
        .finally(() => setLoading(false));
    } else { setLoading(false); }
  }, [token]);

  const login = async (email, password) => {
    const r = await axios.post(`${API}/owner/login`, { email, password });
    localStorage.setItem('owner_token', r.data.token);
    setToken(r.data.token);
    setOwner(r.data.owner);
    return r.data;
  };

  const logout = () => {
    localStorage.removeItem('owner_token');
    setToken(null);
    setOwner(null);
  };

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  return (
    <OwnerCtx.Provider value={{ owner, token, loading, login, logout, headers }}>
      {children}
    </OwnerCtx.Provider>
  );
}

// ─── Login Screen ───
function OwnerLoginScreen() {
  const { login } = useOwner();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success('Bienvenue');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Identifiants incorrects');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0A2540] to-[#0d3050] flex items-center justify-center px-4" data-testid="owner-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-[#D4AF37] rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Anchor className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white">Espace Propriétaire</h1>
          <p className="text-white/50 text-sm mt-1">SOGNUDIMARE — Suivi de votre navire</p>
        </div>
        <Card className="border-0 shadow-2xl">
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  <Input type="email" value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="Votre email" className="pl-10" data-testid="owner-email-input" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Mot de passe</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  <Input type="password" value={password} onChange={e => setPassword(e.target.value)}
                    placeholder="Mot de passe" className="pl-10" data-testid="owner-password-input" />
                </div>
              </div>
              <Button type="submit" disabled={busy} className="w-full bg-[#D4AF37] hover:bg-[#c4a030] text-white" data-testid="owner-login-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Se connecter
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ─── Vessel Calendar ───
function VesselCalendar({ schedule }) {
  const [monthOffset, setMonthOffset] = useState(0);

  const now = new Date();
  const viewDate = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfWeek = new Date(year, month, 1).getDay();

  // Build day status map
  const dayStatus = {};
  for (const s of schedule) {
    const start = new Date(s.start_date);
    const end = new Date(s.end_date);
    let d = new Date(start);
    while (d <= end) {
      if (d.getFullYear() === year && d.getMonth() === month) {
        const day = d.getDate();
        if (s.status === 'charter') dayStatus[day] = 'charter';
        else if (s.status === 'partiel' && dayStatus[day] !== 'charter') dayStatus[day] = 'partiel';
        else if (!dayStatus[day]) dayStatus[day] = 'disponible';
      }
      d.setDate(d.getDate() + 1);
    }
  }

  const statusColor = {
    charter: 'bg-[#C4724F] text-white',
    partiel: 'bg-amber-400 text-white',
    disponible: 'bg-emerald-500 text-white',
  };

  const cells = [];
  for (let i = 0; i < firstDayOfWeek; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <Card data-testid="vessel-calendar">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <Button variant="ghost" size="sm" onClick={() => setMonthOffset(p => p - 1)} data-testid="cal-prev">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <h3 className="text-base font-bold text-[#0A2540]">
            {MOIS_FR[month]} {year}
          </h3>
          <Button variant="ghost" size="sm" onClick={() => setMonthOffset(p => p + 1)} data-testid="cal-next">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        <div className="grid grid-cols-7 gap-1 mb-1">
          {JOURS.map(j => (
            <div key={j} className="text-center text-xs font-medium text-gray-400 py-1">{j}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((day, i) => {
            if (!day) return <div key={`e-${i}`} />;
            const st = dayStatus[day];
            return (
              <div key={day}
                className={`text-center text-sm py-1.5 rounded-lg font-medium transition-colors ${
                  st ? statusColor[st] : 'text-gray-600 hover:bg-gray-100'
                }`}
                data-testid={`cal-day-${day}`}
              >
                {day}
              </div>
            );
          })}
        </div>

        <div className="flex flex-wrap gap-3 mt-4 pt-3 border-t">
          <div className="flex items-center gap-1.5 text-xs">
            <span className="w-3 h-3 rounded bg-[#C4724F]" /> En charter
          </div>
          <div className="flex items-center gap-1.5 text-xs">
            <span className="w-3 h-3 rounded bg-amber-400" /> Partiellement réservé
          </div>
          <div className="flex items-center gap-1.5 text-xs">
            <span className="w-3 h-3 rounded bg-emerald-500" /> Disponible
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Schedule List ───
function ScheduleList({ schedule }) {
  if (!schedule.length) return (
    <Card>
      <CardContent className="p-8 text-center">
        <Calendar className="h-10 w-10 text-gray-300 mx-auto mb-2" />
        <p className="text-sm text-gray-500">Aucune date planifiée pour ce navire</p>
      </CardContent>
    </Card>
  );

  // Calculate total remaining cabins and passengers on partial weeks
  const totalCabinsLeft = schedule.filter(s => s.status === 'partiel')
    .reduce((sum, s) => sum + (s.available_cabins || 0), 0);
  const totalPaxLeft = totalCabinsLeft * 2;

  return (
    <Card data-testid="schedule-list">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-[#0A2540] uppercase tracking-wide flex items-center gap-2">
            <Calendar className="h-4 w-4 text-[#C4724F]" /> Planning détaillé
          </h3>
          {totalCabinsLeft > 0 && (
            <div className="flex items-center gap-1.5 bg-amber-50 border border-amber-200 rounded-full px-3 py-1" data-testid="spots-remaining-badge">
              <Users className="h-3.5 w-3.5 text-amber-600" />
              <span className="text-xs font-bold text-amber-700">{totalCabinsLeft} cabine{totalCabinsLeft > 1 ? 's' : ''} dispo ({totalPaxLeft} pax)</span>
            </div>
          )}
        </div>
        <div className="space-y-2 max-h-[400px] overflow-y-auto">
          {schedule.map((s, i) => {
            const bg = s.status === 'charter' ? 'border-[#C4724F] bg-[#C4724F]/5' :
                       s.status === 'partiel' ? 'border-amber-400 bg-amber-50' :
                       'border-emerald-400 bg-emerald-50';
            const label = s.status === 'charter' ? 'Complet' :
                          s.status === 'partiel' ? `${s.available_cabins} cab. (${s.available_cabins * 2} pax)` :
                          'Disponible';
            const badgeClass = s.status === 'charter' ? 'bg-[#C4724F] text-white' :
                  s.status === 'partiel' ? 'bg-amber-400 text-white' :
                  'bg-emerald-500 text-white';
            return (
              <div key={i} className={`flex items-center justify-between p-3 rounded-lg border-l-4 ${bg}`}>
                <div className="flex items-center gap-3">
                  <div>
                    <p className="text-sm font-medium text-[#0A2540]">{formatDateFr(s.start_date)} → {formatDateFr(s.end_date)}</p>
                    <p className="text-xs text-gray-500">{s.catamaran}{s.client_name ? ` — ${s.client_name}` : ''}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {s.status === 'partiel' && (
                    <div className="flex gap-0.5">
                      {Array.from({ length: s.total_cabins || 4 }).map((_, j) => (
                        <div key={j} className={`w-2 h-5 rounded-sm ${j < (s.total_cabins || 4) - (s.available_cabins || 0) ? 'bg-[#C4724F]' : 'bg-amber-300'}`} />
                      ))}
                    </div>
                  )}
                  {s.status === 'charter' && (
                    <div className="flex gap-0.5">
                      {Array.from({ length: s.total_cabins || 4 }).map((_, j) => (
                        <div key={j} className="w-2 h-5 rounded-sm bg-[#C4724F]" />
                      ))}
                    </div>
                  )}
                  <Badge className={badgeClass}>{label}</Badge>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Live Map (MarineTraffic) ───
function VesselLiveMap({ mmsi }) {
  if (!mmsi) return (
    <Card>
      <CardContent className="p-8 text-center">
        <Navigation className="h-10 w-10 text-gray-300 mx-auto mb-2" />
        <p className="text-sm text-gray-500">Position GPS non disponible (MMSI non configuré)</p>
      </CardContent>
    </Card>
  );

  return (
    <Card data-testid="vessel-live-map">
      <CardContent className="p-0 overflow-hidden rounded-xl">
        <div className="bg-[#0A2540] px-5 py-3 flex items-center gap-2">
          <Navigation className="h-4 w-4 text-[#D4AF37]" />
          <h3 className="text-sm font-bold text-white">Position en direct</h3>
          <span className="ml-auto text-xs text-white/40">MMSI : {mmsi}</span>
        </div>
        <iframe
          title="Position du navire"
          width="100%"
          height="400"
          src={`https://www.marinetraffic.com/en/ais/embed/zoom:10/centery:42/centerx:9/maptype:4/shownames:true/mmsi:${mmsi}/rememberhidden:false`}
          frameBorder="0"
          className="w-full"
          data-testid="marinetraffic-iframe"
        />
      </CardContent>
    </Card>
  );
}

// ─── Contract Conditions Visual ───
function ContractConditionsVisual({ contract }) {
  if (!contract) return null;
  const c = contract;
  const cc = c.commission_config || {};
  const fixe = c.total_amount || 0;
  const rate = cc.rate || 0;
  const threshold = cc.passenger_threshold || 0;
  const avgPrice = cc.avg_price_per_passenger || 0;
  const maxCap = cc.max_capacity_per_week || 8;
  const commissionPerPax = avgPrice * rate / 100;
  const isSigned = !!c.owner_signed_at;
  const signUrl = c.sign_token ? `/contrat-proprietaire/${c.sign_token}` : null;
  const pdfUrl = c.sign_token ? `${API}/owner-contract/public/${c.sign_token}/pdf` : null;

  const statusLabels = {
    draft: 'Brouillon', sent: 'En attente', signed_owner: 'Signé (vous)',
    signed_exploitant: 'Signé (exploitant)', signed: 'Signé (complet)',
  };

  const formatEur = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);

  return (
    <Card data-testid="contract-conditions-visual">
      <CardContent className="p-0 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#0A2540] to-[#0d3050] p-4 sm:p-5 text-white">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-[#D4AF37] flex-shrink-0" />
              <h3 className="font-bold text-sm sm:text-base">Conditions contractuelles</h3>
            </div>
            <span className="text-xs bg-white/10 px-3 py-1 rounded-full w-fit">{c.reference} — Saison {c.season}</span>
          </div>
          <p className="text-white/60 text-xs mb-4">
            Contrat de mise à disposition — {c.vessel?.name} ({c.vessel?.model}) — {statusLabels[c.status] || c.status}
          </p>
          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row gap-2">
            {pdfUrl && (
              <Button size="sm" variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20 gap-1.5 animate-pulse" onClick={() => window.open(pdfUrl, '_blank')} data-testid="owner-download-contract-pdf">
                <FileDown className="h-4 w-4" /> Consulter le contrat (PDF)
              </Button>
            )}
            {!isSigned && signUrl && (
              <Button size="sm" className="bg-[#D4AF37] hover:bg-[#c4a030] text-white gap-1.5 animate-bounce" onClick={() => window.open(signUrl, '_blank')} data-testid="owner-sign-contract-btn">
                <FileSignature className="h-4 w-4" /> Signer le contrat
              </Button>
            )}
            {isSigned && (
              <div className="flex items-center gap-1.5 bg-emerald-500/20 border border-emerald-400/30 text-emerald-300 px-3 py-1.5 rounded-lg text-xs font-medium">
                <Check className="h-3.5 w-3.5" /> Contrat signé le {new Date(c.owner_signed_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}
              </div>
            )}
          </div>
        </div>

        <div className="p-5 space-y-6">
          {/* Parties */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="bg-[#C4724F]/5 border border-[#C4724F]/20 rounded-xl p-4">
              <p className="text-[10px] font-bold text-[#C4724F] uppercase tracking-wider mb-1">Propriétaire</p>
              <p className="font-bold text-[#0A2540] text-sm">{c.owner?.name}</p>
              {c.owner?.address && <p className="text-xs text-gray-500 mt-0.5">{c.owner.address}</p>}
            </div>
            <div className="bg-[#0A2540]/5 border border-[#0A2540]/20 rounded-xl p-4">
              <p className="text-[10px] font-bold text-[#0A2540] uppercase tracking-wider mb-1">Exploitant</p>
              <p className="font-bold text-[#0A2540] text-sm">{c.exploitant?.name}</p>
              <p className="text-xs text-gray-500 mt-0.5">{c.exploitant?.company}</p>
            </div>
          </div>

          {/* Financial model visual */}
          <div>
            <p className="text-xs font-bold text-[#0A2540] uppercase tracking-wider mb-3">Modèle de rémunération</p>
            <div className="bg-gray-50 rounded-xl p-4 border">
              {/* Fixed part */}
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-[#D4AF37] rounded-xl flex items-center justify-center flex-shrink-0">
                  <CreditCard className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-bold text-[#0A2540]">Partie fixe garantie</p>
                    <p className="text-lg font-black text-[#D4AF37]">{formatEur(fixe)}</p>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5 mt-1.5">
                    <div className="bg-[#D4AF37] h-2.5 rounded-full" style={{ width: '100%' }} />
                  </div>
                </div>
              </div>

              {/* Payment schedule */}
              {c.payment_schedule?.length > 0 && (
                <div className="sm:ml-[60px] pl-4 border-l-2 border-[#D4AF37]/30 space-y-2 mb-4">
                  {c.payment_schedule.map((p, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${p.paid ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                        <span className="text-xs text-gray-600">{p.label}</span>
                      </div>
                      <span className={`text-xs font-bold ${p.paid ? 'text-emerald-600' : 'text-gray-700'}`}>
                        {formatEur(p.amount)} {p.paid ? '✓' : ''}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Divider with + */}
              {cc.enabled && (
                <>
                  <div className="flex items-center gap-3 my-3">
                    <div className="flex-1 h-px bg-gray-200" />
                    <span className="text-xs font-bold text-[#C4724F] bg-[#C4724F]/10 w-6 h-6 rounded-full flex items-center justify-center">+</span>
                    <div className="flex-1 h-px bg-gray-200" />
                  </div>

                  {/* Variable part */}
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-[#C4724F] rounded-xl flex items-center justify-center flex-shrink-0">
                      <Users className="h-6 w-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-bold text-[#0A2540]">Partie variable (commission)</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        <strong className="text-[#C4724F]">{rate}%</strong> du CA par passager au-delà de <strong>{threshold} passagers</strong>
                      </p>
                    </div>
                  </div>

                  {/* Commission details */}
                  <div className="mt-3 sm:ml-[60px] grid grid-cols-3 gap-2">
                    <div className="bg-white rounded-lg p-2.5 border text-center">
                      <p className="text-lg font-black text-[#C4724F]">{rate}%</p>
                      <p className="text-[10px] text-gray-500">Taux commission</p>
                    </div>
                    <div className="bg-white rounded-lg p-2.5 border text-center">
                      <p className="text-lg font-black text-[#0A2540]">{threshold}</p>
                      <p className="text-[10px] text-gray-500">Seuil passagers</p>
                    </div>
                    <div className="bg-white rounded-lg p-2.5 border text-center">
                      <p className="text-lg font-black text-emerald-600">{formatEur(commissionPerPax)}</p>
                      <p className="text-[10px] text-gray-500">Par pax suppl.</p>
                    </div>
                  </div>

                  {/* How it works */}
                  <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-xs font-bold text-amber-800 mb-1.5">Comment ça fonctionne</p>
                    <div className="space-y-1.5 text-xs text-amber-700">
                      <div className="flex items-start gap-2">
                        <span className="bg-amber-300 text-amber-900 w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">1</span>
                        <span>Les <strong>{threshold} premiers passagers</strong> couvrent la partie fixe de {formatEur(fixe)}</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="bg-amber-300 text-amber-900 w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">2</span>
                        <span>À partir du <strong>{threshold + 1}ème passager</strong>, vous recevez <strong>{rate}%</strong> du CA généré</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="bg-amber-300 text-amber-900 w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">3</span>
                        <span>Soit <strong>{formatEur(commissionPerPax)} par passager</strong> supplémentaire (base {formatEur(avgPrice)}/pax)</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="bg-amber-300 text-amber-900 w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">4</span>
                        <span>Capacité max : <strong>{maxCap} passagers/semaine</strong> — versement en fin de saison</span>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Season timeline */}
          <div>
            <p className="text-xs font-bold text-[#0A2540] uppercase tracking-wider mb-3">Période contractuelle</p>
            <div className="bg-gray-50 rounded-xl p-4 border">
              <div className="flex items-center justify-between mb-2">
                <div className="text-center">
                  <p className="text-xs text-gray-500">Début</p>
                  <p className="text-sm font-bold text-[#0A2540]">{c.start_date || '—'}</p>
                </div>
                <div className="flex-1 mx-4 relative">
                  <div className="h-2 bg-gradient-to-r from-[#0A2540] via-[#C4724F] to-[#D4AF37] rounded-full" />
                  <Ship className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-5 w-5 text-white drop-shadow-md" />
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-500">Fin</p>
                  <p className="text-sm font-bold text-[#0A2540]">{c.end_date || '—'}</p>
                </div>
              </div>
              {c.vessel?.home_port && (
                <p className="text-xs text-gray-500 text-center mt-2">
                  Port d'attache : <strong>{c.vessel.home_port}</strong> — Récupération et retour par le Capitaine
                </p>
              )}
              <p className="text-xs text-gray-400 text-center mt-1">
                Zone de navigation : {c.navigation_zone || "Côte d'Azur, Corse et Sardaigne"}
              </p>
            </div>
          </div>

          {/* Key articles summary */}
          <div>
            <p className="text-xs font-bold text-[#0A2540] uppercase tracking-wider mb-3">Articles clés du contrat</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {[
                { icon: '⚓', title: 'Obligations Exploitant', desc: 'Commercialisation, carburant, équipage, avitaillement, entretien courant' },
                { icon: '🛟', title: 'Obligations Propriétaire', desc: 'Navire en état, réparations majeures, assurance valide, disponibilité' },
                { icon: '🔧', title: 'Entretien', desc: 'Courant = Exploitant / Réparations majeures = Propriétaire' },
                { icon: '🛡', title: 'Assurances', desc: `Navire: ${c.owner?.insurance_company || '—'} / RC Pro: ${c.exploitant?.insurance_company || '—'}` },
                { icon: '📋', title: 'État des lieux', desc: 'Inventaire détaillé à la remise et à la restitution du navire' },
                { icon: '⚖️', title: 'Juridiction', desc: `Tribunal compétent : ${c.jurisdiction || 'Ajaccio'}` },
              ].map((a, i) => (
                <div key={i} className="flex items-start gap-2.5 p-2.5 rounded-lg bg-gray-50 border">
                  <span className="text-base flex-shrink-0">{a.icon}</span>
                  <div>
                    <p className="text-xs font-bold text-[#0A2540]">{a.title}</p>
                    <p className="text-[11px] text-gray-500 leading-tight">{a.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Contract Summary ───
function ContractSummary({ contracts }) {
  if (!contracts.length) return null;

  const statusLabels = {
    draft: 'Brouillon', sent: 'Envoyé', signed_owner: 'Signé (vous)',
    signed_exploitant: 'Signé (exploitant)', signed: 'Signé (complet)',
  };
  const statusColors = {
    draft: 'bg-slate-100 text-slate-700', sent: 'bg-blue-100 text-blue-700',
    signed_owner: 'bg-amber-100 text-amber-700', signed_exploitant: 'bg-indigo-100 text-indigo-700',
    signed: 'bg-emerald-100 text-emerald-700',
  };

  return (
    <Card data-testid="owner-contracts-list">
      <CardContent className="p-5">
        <h3 className="text-sm font-bold text-[#0A2540] uppercase tracking-wide mb-3 flex items-center gap-2">
          <FileText className="h-4 w-4 text-[#C4724F]" /> Mes contrats
        </h3>
        <div className="space-y-2">
          {contracts.map(c => (
            <div key={c.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 rounded-lg bg-gray-50 border">
              <div>
                <p className="text-sm font-medium text-[#0A2540]">{c.vessel?.name} — {c.vessel?.model}</p>
                <p className="text-xs text-gray-500">{c.reference} — Saison {c.season}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-[#0A2540]">
                  {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(c.total_amount || 0)}
                </span>
                <Badge className={statusColors[c.status] || statusColors.draft}>
                  {statusLabels[c.status] || c.status}
                </Badge>
              </div>
            </div>
          ))}
        </div>
        {/* Payment schedule for the first active contract */}
        {contracts[0]?.payment_schedule?.length > 0 && (
          <div className="mt-4 pt-3 border-t">
            <p className="text-xs font-bold text-[#0A2540] uppercase mb-2 flex items-center gap-1">
              <CreditCard className="h-3 w-3 text-[#C4724F]" /> Échéancier
            </p>
            {contracts[0].payment_schedule.map((p, i) => (
              <div key={i} className={`flex items-center justify-between p-2 rounded mb-1 ${p.paid ? 'bg-emerald-50' : 'bg-gray-50'}`}>
                <div className="flex items-center gap-2">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${p.paid ? 'bg-emerald-500 text-white' : 'bg-gray-300 text-white'}`}>
                    {p.paid ? <Check className="h-3 w-3" /> : i + 1}
                  </div>
                  <span className="text-xs">{p.label}</span>
                </div>
                <span className={`text-xs font-medium ${p.paid ? 'text-emerald-600' : 'text-gray-600'}`}>
                  {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(p.amount || 0)}
                  {p.paid ? ' — Payé' : ''}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Dashboard ───
function OwnerDashboard() {
  const { owner, logout, token } = useOwner();
  const [schedule, setSchedule] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [financialData, setFinancialData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    
    const loadData = async () => {
      try {
        const [sched, contr] = await Promise.all([
          axios.get(`${API}/owner/vessel-schedule`, { headers }),
          axios.get(`${API}/owner/contracts`, { headers }),
        ]);
        setSchedule(sched.data.schedule || []);
        const contractsList = contr.data || [];
        setContracts(contractsList);
        
        // Fetch financial data for ALL contracts (check commission on each)
        const financials = {};
        console.log(`[OwnerPortal] Owner: ${owner?.email} | Vessels: ${owner?.vessel_names?.join(', ')}`);
        console.log(`[OwnerPortal] ${contractsList.length} contracts found`);
        for (const ct of contractsList) {
          console.log(`[OwnerPortal] Fetching financial for ${ct.vessel?.name} (${ct.id})`);
          try {
            const f = await axios.get(`${API}/owner/contracts/${ct.id}/financial`, { headers });
            console.log(`[OwnerPortal] Financial response for ${ct.id}: enabled=${f.data?.enabled}`);
            if (f.data?.enabled) {
              financials[ct.id] = f.data;
            }
          } catch (err) {
            console.error(`[OwnerPortal] Financial ERROR for ${ct.id}:`, err.response?.status, err.response?.data);
          }
        }
        setFinancialData(financials);
      } catch (err) {
        toast.error('Erreur de chargement');
      } finally { setLoading(false); }
    };
    loadData();
  }, [token]);

  const mmsi = owner?.mmsi || '';

  if (loading) return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
      <Loader2 className="h-8 w-8 text-[#C4724F] animate-spin" />
    </div>
  );

  // Stats
  const charterWeeks = schedule.filter(s => s.status === 'charter').length;
  const availableWeeks = schedule.filter(s => s.status === 'disponible').length;
  const partialWeeks = schedule.filter(s => s.status === 'partiel').length;
  const totalCabinsLeftMain = schedule.filter(s => s.status === 'partiel')
    .reduce((sum, s) => sum + (s.available_cabins || 0), 0);
  const totalWeeks = charterWeeks + availableWeeks + partialWeeks;
  const occupancyRate = totalWeeks > 0 ? Math.round(((charterWeeks + partialWeeks * 0.5) / totalWeeks) * 100) : 0;

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="owner-dashboard">
      {/* Header with wave effect */}
      <div className="bg-gradient-to-br from-[#0A2540] via-[#0d3050] to-[#0A2540] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <svg viewBox="0 0 1440 320" className="absolute bottom-0 w-full"><path fill="#D4AF37" d="M0,224L48,213.3C96,203,192,181,288,181.3C384,181,480,203,576,218.7C672,235,768,245,864,234.7C960,224,1056,192,1152,181.3C1248,171,1344,181,1392,186.7L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z" /></svg>
        </div>
        <div className="max-w-6xl mx-auto px-4 py-6 relative z-10">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-[#D4AF37] rounded-xl flex items-center justify-center shadow-lg shadow-[#D4AF37]/20">
                <Anchor className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-white font-bold text-xl">Espace Propriétaire</h1>
                <p className="text-[#D4AF37] text-sm font-medium">{owner?.name}</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={logout} className="text-white/70 hover:text-white hover:bg-white/10 gap-1" data-testid="owner-logout-btn">
              <LogOut className="h-4 w-4" /> Déconnexion
            </Button>
          </div>

          {/* Vessel hero card */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 sm:p-5 border border-white/10">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex items-center gap-3">
                <Ship className="h-8 sm:h-10 w-8 sm:w-10 text-[#D4AF37] flex-shrink-0" />
                <div>
                  <p className="text-white font-bold text-lg sm:text-2xl">{(owner?.vessel_names || []).join(', ') || 'Mon navire'}</p>
                  {mmsi && <p className="text-white/40 text-xs font-mono">MMSI {mmsi}</p>}
                </div>
              </div>
              <div className="sm:ml-auto flex gap-6">
                <div className="text-center">
                  <p className="text-2xl sm:text-3xl font-black text-[#D4AF37]">{occupancyRate}%</p>
                  <p className="text-white/50 text-[10px] sm:text-xs">taux occupation</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl sm:text-3xl font-black text-white">{totalWeeks}</p>
                  <p className="text-white/50 text-[10px] sm:text-xs">semaines saison</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Stats cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 -mt-2">
          <Card className="border-l-4 border-l-[#C4724F] shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-black text-[#C4724F]">{charterWeeks}</p>
                  <p className="text-xs text-gray-500 mt-0.5">En charter</p>
                </div>
                <div className="w-10 h-10 bg-[#C4724F]/10 rounded-xl flex items-center justify-center">
                  <Compass className="h-5 w-5 text-[#C4724F]" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-emerald-500 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-black text-emerald-500">{availableWeeks}</p>
                  <p className="text-xs text-gray-500 mt-0.5">Disponibles</p>
                </div>
                <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center">
                  <Sun className="h-5 w-5 text-emerald-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-amber-400 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-black text-amber-500">{partialWeeks}</p>
                  <p className="text-xs text-gray-500 mt-0.5">Partiellement réservé</p>
                </div>
                <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center">
                  <Waves className="h-5 w-5 text-amber-400" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-blue-500 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-black text-blue-600">{totalCabinsLeftMain}</p>
                  <p className="text-xs text-gray-500 mt-0.5">Cabines dispo ({totalCabinsLeftMain * 2} pax)</p>
                </div>
                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                  <Users className="h-5 w-5 text-blue-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Calendar + Live Map */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <VesselCalendar schedule={schedule} />
          <VesselLiveMap mmsi={mmsi} />
        </div>

        {/* Schedule list */}
        <ScheduleList schedule={schedule} />

        {/* Contracts */}
        <ContractSummary contracts={contracts} />

        {/* Contract Conditions Visual */}
        {contracts.length > 0 && <ContractConditionsVisual contract={contracts[0]} />}

        {/* Financial Dashboards */}
        {contracts.filter(ct => financialData[ct.id]?.enabled).map(ct => (
          <div key={ct.id}>
            <FinancialDashboard data={financialData[ct.id]} viewMode="owner" />
          </div>
        ))}

        {/* Footer */}
        <p className="text-center text-gray-400 text-xs pb-4">
          SOGNUDIMARE SARL — Croisières en catamaran — Corse & Méditerranée
        </p>
      </div>
    </div>
  );
}

// ─── Main Portal ───
export default function OwnerPortal() {
  return (
    <OwnerProvider>
      <OwnerPortalInner />
    </OwnerProvider>
  );
}

function OwnerPortalInner() {
  const { owner, loading } = useOwner();

  if (loading) return (
    <div className="min-h-screen bg-[#0A2540] flex items-center justify-center">
      <Loader2 className="h-8 w-8 text-[#D4AF37] animate-spin" />
    </div>
  );

  return owner ? <OwnerDashboard /> : <OwnerLoginScreen />;
}
